import { createFileRoute, Link, useNavigate, Outlet, useMatches } from "@tanstack/react-router";
import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  Layers,
  Table2,
  Variable,
  FormInput,
  Calculator,
  Lock,
  Pencil,
  Save,
  X,
  Plus,
  Palette,
  Eye,
  FlaskConical,
  Settings,
} from "lucide-react";
import { toast } from "sonner";

type AdminSimulatorSearch = {
  tab?: string;
  version?: number;
};

export const Route = createFileRoute('/_dashboard/admin/simulators/$id')({
  component: AdminSimulatorConfigPage,
  validateSearch: (search: Record<string, unknown>): AdminSimulatorSearch => ({
    tab: search.tab as string | undefined,
    version: search.version ? Number(search.version) : undefined,
  }),
});

import { Header } from "@/components/layout/Header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { getSimulator, updateSimulator } from "@/lib/api/simulators";
import { createVersion } from "@/lib/api/versions";
import { useIsAdmin } from "@/lib/auth/store";
import { AdminGuard } from "@/lib/auth/AdminGuard";
import { getErrorMessage } from "@/lib/api/client";
import { formatDate } from "@/lib/utils";

import { VersionsTab } from "@/components/simulators/tabs/VersionsTab";
import { ReferencesTab } from "@/components/simulators/tabs/ReferencesTab";
import { VariablesTab } from "@/components/simulators/tabs/VariablesTab";
import { FormFieldsTab } from "@/components/simulators/tabs/FormFieldsTab";
import { OutputFieldsTab } from "@/components/simulators/tabs/OutputFieldsTab";
import { AppearanceTab } from "@/components/simulators/tabs/AppearanceTab";
import { SettingsTab } from "@/components/simulators/tabs/SettingsTab";
import { AccessTab } from "@/components/simulators/tabs/AccessTab";
import { TestScenariosTab } from "@/components/simulators/tabs/TestScenariosTab";
import { ProposalTemplatesTab } from "@/components/simulators/tabs/ProposalTemplatesTab";

const VALID_TABS = ["versions", "references", "variables", "form-fields", "output-fields", "test-scenarios", "appearance", "settings", "access", "templates"];

function AdminSimulatorConfigPage() {
  const { id } = Route.useParams();
  const searchParams = Route.useSearch();
  const navigate = useNavigate();
  const simulatorId = Number(id);
  const isAdmin = useIsAdmin();
  const queryClient = useQueryClient();
  const matches = useMatches();

  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState("");
  const [editSlug, setEditSlug] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [isCreatingVersion, setIsCreatingVersion] = useState(false);
  
  // Get version from URL
  const urlVersionId = searchParams.version ? Number(searchParams.version) : null;
  
  // State for selected version - initialize from URL if available
  const [selectedVersionId, setSelectedVersionId] = useState<number | null>(urlVersionId);
  
  // State for controlled tabs
  // Initialize from URL or default to "versions"
  const initialTab = searchParams.tab && VALID_TABS.includes(searchParams.tab) ? searchParams.tab : "versions";
  const [activeTab, setActiveTab] = useState(initialTab);
  
  // Sync activeTab with URL changes (e.g., browser back/forward)
  useEffect(() => {
    if (searchParams.tab && VALID_TABS.includes(searchParams.tab) && searchParams.tab !== activeTab) {
      setActiveTab(searchParams.tab);
    }
  }, [searchParams.tab, activeTab]);
  
  // Sync selectedVersionId with URL changes (e.g., browser back/forward)
  useEffect(() => {
    const urlVersionId = searchParams.version ? Number(searchParams.version) : null;
    if (urlVersionId && urlVersionId !== selectedVersionId) {
      setSelectedVersionId(urlVersionId);
    }
  }, [searchParams.version, selectedVersionId]);
  
  // Check if we're on a child route (e.g., /templates/new)
  const isChildRoute = matches.some(match => 
    match.routeId.includes('/templates/')
  );
  
  // Update URL when tab changes
  const handleTabChange = (value: string) => {
    setActiveTab(value);
    navigate({
      to: '/admin/simulators/$id',
      params: { id },
      search: { ...searchParams, tab: value },
      replace: true,
    });
  };
  
  // Update URL when version changes
  const handleVersionSelect = (versionId: number) => {
    setSelectedVersionId(versionId);
    navigate({
      to: '/admin/simulators/$id',
      params: { id },
      search: { ...searchParams, version: versionId },
      replace: true,
    });
  };

  const { data: simulator, isLoading } = useQuery({
    queryKey: ["simulator", simulatorId],
    queryFn: () => getSimulator(simulatorId),
    enabled: !!simulatorId,
  });

  const updateMutation = useMutation({
    mutationFn: (data: { name?: string; slug?: string; description?: string }) =>
      updateSimulator(simulatorId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["simulator", simulatorId] });
      queryClient.invalidateQueries({ queryKey: ["simulators"] });
      toast.success("Simulateur mis à jour");
      setIsEditing(false);
    },
    onError: (error) => {
      toast.error("Erreur", { description: getErrorMessage(error) });
    },
  });

  const createVersionMutation = useMutation({
    mutationFn: () => createVersion(simulatorId, { notes: "Version initiale" }),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["simulator", simulatorId] });
      toast.success("Version créée");
      handleVersionSelect(data.id);
      setIsCreatingVersion(false);
    },
    onError: (error) => {
      toast.error("Erreur", { description: getErrorMessage(error) });
      setIsCreatingVersion(false);
    },
  });

  const handleStartEdit = () => {
    if (simulator) {
      setEditName(simulator.name);
      setEditSlug(simulator.slug);
      setEditDescription(simulator.description || "");
      setIsEditing(true);
    }
  };

  const handleSaveEdit = () => {
    updateMutation.mutate({
      name: editName,
      slug: editSlug || undefined,
      description: editDescription || undefined,
    });
  };

  // Set selected version when simulator loads (only if not already set from URL)
  // Selects the most recent version (highest version_number)
  // Don't navigate if we're on a child route (e.g., template editor)
  useEffect(() => {
    if (simulator && simulator.versions.length > 0 && !selectedVersionId && !isChildRoute) {
      const mostRecentVersion = [...simulator.versions].sort((a, b) => b.version_number - a.version_number)[0];
      if (mostRecentVersion) {
        handleVersionSelect(mostRecentVersion.id);
      }
    }
  }, [simulator, isChildRoute]);

  const selectedVersion = simulator?.versions.find((v) => v.id === selectedVersionId);

  // If on child route, render Outlet for the child (must be after all hooks)
  if (isChildRoute) {
    return <Outlet />;
  }

  if (isLoading) {
    return (
      <div className="flex flex-col h-full">
        <Header title="Configuration simulateur" />
        <div className="flex-1 p-6 space-y-6">
          <div className="flex items-center gap-4">
            <Skeleton className="h-10 w-10" />
            <div className="space-y-2">
              <Skeleton className="h-8 w-64" />
              <Skeleton className="h-4 w-96" />
            </div>
          </div>
          <Skeleton className="h-[600px] w-full" />
        </div>
      </div>
    );
  }

  if (!simulator) {
    return (
      <div className="flex flex-col h-full">
        <Header title="Configuration simulateur" />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <h2 className="text-2xl font-bold mb-2">Simulateur non trouvé</h2>
            <p className="text-muted-foreground mb-4">
              Ce simulateur n&apos;existe pas ou a été supprimé.
            </p>
            <Button asChild>
              <Link to="/admin/simulators">Retour à la configuration</Link>
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <AdminGuard>
    <div className="flex flex-col h-full">
      <Header title="Configuration simulateur" />

      <div className="flex-1 p-6 space-y-6 overflow-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex flex-col sm:flex-row sm:items-start justify-between gap-4"
        >
          <div className="flex items-start gap-4">
            <Button variant="ghost" size="icon" asChild>
              <Link to="/admin/simulators">
                <ArrowLeft className="h-4 w-4" />
              </Link>
            </Button>

            <div className="flex-1">
              {isEditing ? (
                <div className="space-y-3">
                  <Input
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    className="text-2xl font-bold h-auto py-1"
                  />
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground whitespace-nowrap">Slug:</span>
                    <Input
                      value={editSlug}
                      onChange={(e) => setEditSlug(e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, '_'))}
                      className="font-mono text-sm h-8"
                      placeholder="slug_du_simulateur"
                    />
                  </div>
                  <Textarea
                    value={editDescription}
                    onChange={(e) => setEditDescription(e.target.value)}
                    placeholder="Description..."
                    rows={2}
                  />
                  <div className="flex gap-2">
                    <Button size="sm" onClick={handleSaveEdit} disabled={updateMutation.isPending}>
                      <Save className="mr-2 h-4 w-4" />
                      Enregistrer
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setIsEditing(false)}>
                      <X className="mr-2 h-4 w-4" />
                      Annuler
                    </Button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="flex items-center gap-3">
                    <h1 className="text-2xl font-bold">{simulator.name}</h1>
                    <Badge
                      variant={simulator.status === "active" ? "success" : "secondary"}
                    >
                      {simulator.status === "active" ? "Actif" : "Archivé"}
                    </Badge>
                    {isAdmin && (
                      <Button variant="ghost" size="icon" onClick={handleStartEdit}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                  {simulator.description && (
                    <p className="text-muted-foreground mt-1">{simulator.description}</p>
                  )}
                  <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground">
                    <span>Slug: <code className="px-1 py-0.5 bg-muted rounded">{simulator.slug}</code></span>
                    <span>Mis à jour: {formatDate(simulator.updated_at)}</span>
                  </div>
                </>
              )}
            </div>
          </div>

          </motion.div>

        {/* Version selector */}
        {simulator.versions.length > 0 && (
          <Card>
            <CardHeader className="py-4">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-base">Version active</CardTitle>
                  <CardDescription>
                    Sélectionnez la version à configurer
                  </CardDescription>
                </div>
                <select
                  value={selectedVersionId || ""}
                  onChange={(e) => handleVersionSelect(Number(e.target.value))}
                  className="px-3 py-2 rounded-md border bg-background"
                >
                  {simulator.versions.map((version) => (
                    <option key={version.id} value={version.id}>
                      Version {version.version_number} -{" "}
                      {version.status === "draft"
                        ? "Brouillon"
                        : version.status === "published"
                        ? "Publiée"
                        : "Archivée"}
                    </option>
                  ))}
                </select>
              </div>
            </CardHeader>
          </Card>
        )}

        {/* Tabs */}
        {selectedVersion ? (
          <>
          <Tabs value={activeTab} onValueChange={handleTabChange} className="space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <TabsList className="flex-wrap h-auto gap-1 p-1">
              <TabsTrigger value="versions" className="gap-2">
                <Layers className="h-4 w-4" />
                Versions
              </TabsTrigger>
              <TabsTrigger value="references" className="gap-2">
                <Table2 className="h-4 w-4" />
                Tables de référence
              </TabsTrigger>
              <TabsTrigger value="variables" className="gap-2">
                <Variable className="h-4 w-4" />
                Variables
              </TabsTrigger>
              <TabsTrigger value="form-fields" className="gap-2">
                <FormInput className="h-4 w-4" />
                Formulaire
              </TabsTrigger>
              <TabsTrigger value="output-fields" className="gap-2">
                <Calculator className="h-4 w-4" />
                Calculs
              </TabsTrigger>
              <TabsTrigger value="test-scenarios" className="gap-2">
                <FlaskConical className="h-4 w-4" />
                Tests
              </TabsTrigger>
              <TabsTrigger value="appearance" className="gap-2">
                <Palette className="h-4 w-4" />
                Apparence
              </TabsTrigger>
              <TabsTrigger value="settings" className="gap-2">
                <Settings className="h-4 w-4" />
                Paramètres
              </TabsTrigger>
              <TabsTrigger value="access" className="gap-2">
                <Lock className="h-4 w-4" />
                Accès
              </TabsTrigger>
              <TabsTrigger value="templates" className="gap-2">
                <Layers className="h-4 w-4" />
                Templates de proposition
              </TabsTrigger>
            </TabsList>

              <Button
                variant="outline"
                asChild
                disabled={!selectedVersionId}
              >
                <Link 
                  to="/simulators/$id/simulate" 
                  params={{ id: String(simulatorId) }}
                  search={{ preview: "true", version: selectedVersionId || undefined }}
                >
                  <Eye className="mr-2 h-4 w-4" />
                  Aperçu
                </Link>
              </Button>
            </div>

            <TabsContent value="versions">
              <VersionsTab
                simulatorId={simulatorId}
                versions={simulator.versions}
                selectedVersionId={selectedVersionId}
                onVersionSelect={handleVersionSelect}
              />
            </TabsContent>

            <TabsContent value="references">
              <ReferencesTab
                versionId={selectedVersionId!}
                isEditable={selectedVersion.status === "draft"}
              />
            </TabsContent>

            <TabsContent value="variables">
              <VariablesTab
                versionId={selectedVersionId!}
                isEditable={selectedVersion.status === "draft"}
              />
            </TabsContent>

            <TabsContent value="form-fields">
              <FormFieldsTab
                versionId={selectedVersionId!}
                isEditable={selectedVersion.status === "draft"}
              />
            </TabsContent>

            <TabsContent value="output-fields">
              <OutputFieldsTab
                versionId={selectedVersionId!}
                isEditable={selectedVersion.status === "draft"}
              />
            </TabsContent>

            <TabsContent value="test-scenarios">
              <TestScenariosTab
                versionId={selectedVersionId!}
                isEditable={selectedVersion.status === "draft"}
              />
            </TabsContent>

            <TabsContent value="appearance">
              <AppearanceTab
                versionId={selectedVersionId!}
                simulatorId={simulatorId}
                isEditable={selectedVersion.status === "draft"}
              />
            </TabsContent>

            <TabsContent value="settings">
              <SettingsTab
                versionId={selectedVersionId!}
                simulatorId={simulatorId}
                isEditable={selectedVersion.status === "draft"}
              />
            </TabsContent>

            <TabsContent value="access">
              <AccessTab
                versionId={selectedVersionId!}
                isEditable={isAdmin}
              />
            </TabsContent>

            <TabsContent value="templates">
              <ProposalTemplatesTab
                simulatorId={simulatorId}
                versionId={selectedVersionId!}
                isEditable={isAdmin}
              />
            </TabsContent>
          </Tabs>
          </>
        ) : (
          <Card>
            <CardContent className="py-12 text-center">
              <Layers className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
              <h3 className="text-lg font-semibold mb-2">Aucune version</h3>
              <p className="text-muted-foreground mb-4">
                Ce simulateur n&apos;a pas encore de version. Créez-en une pour commencer.
              </p>
              {isAdmin && (
                <Button
                  onClick={() => {
                    setIsCreatingVersion(true);
                    createVersionMutation.mutate();
                  }}
                  disabled={createVersionMutation.isPending}
                >
                  <Plus className="mr-2 h-4 w-4" />
                  {createVersionMutation.isPending ? "Création..." : "Créer la première version"}
                </Button>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
    </AdminGuard>
  );
}

